import React from "react";
import { Container } from "react-bootstrap";
import Particle from "../Particle";
import { useTranslation } from "react-i18next";
import "./Experience.css";

const timeline = [
  {
    company: "Confidential Client",
    role: "AI Developer",
    start: "Sep 2025",
    end: "Feb 2026",
    location: "Remote · Freelance",
    bullets: [
      "Designed and adapted a tailored AI assistant working with the company's existing accumulated data.",
      "Built natural language interface for data retrieval, generation, and business process support.",
      "Proposed AI-driven alternative to avoid a costly migration of legacy business software.",
      "Added backend endpoints, deployed dedicated AI machine, and created custom AI tools for internal workflows.",
      "Optimized slow database queries and improved backend performance to support AI-assisted UX.",
    ],
    tech: ["Python", "LLMs", "FastAPI", "REST API", "Docker"],
  },
  {
    company: "Veridas",
    role: "Full Stack / Mobile & AI Developer",
    start: "Mar 2025",
    end: null,
    location: "Remote · Freelance",
    bullets: [
      "Next-gen digital identity platform (mobile + backend + web) with AI image analysis.",
      "Modernizing app architecture: scalability, maintainability and code quality improvements.",
      "Redesigned UX for a smoother and more reliable identity verification flow.",
      "Built reusable components for document capture, liveness detection and onboarding.",
      "Refactored legacy modules and aligned codebase with modern architectural patterns.",
    ],
    tech: ["Kotlin", "Kotlin Multiplatform", "Java", "Python", "LLMs", "React", "AI Image Analysis"],
  },
  {
    company: "Bluumi",
    role: "Full Stack / Mobile Developer — Android, iOS & Flutter",
    start: "Mar 2024",
    end: null,
    location: "Remote",
    bullets: [
      "New features in Android apps across health, industrial, retail and sport sectors with Jetpack Compose.",
      "Project modernization: ButterKnife & Synthetics → ViewBinding; SDK and dependency updates.",
      "Flutter apps built from scratch for retail and entertainment using BLoC architecture.",
      "API integration with Retrofit and local persistence with Room.",
    ],
    tech: ["Kotlin", "Java", "Jetpack Compose", "Dart", "Flutter", "BLoC", "Retrofit", "Room", "SQL"],
  },
  {
    company: "iTom",
    role: "AI Engineer / Mobile Developer",
    start: "Mar 2023",
    end: null,
    location: "Remote · Freelance",
    bullets: [
      "AI-integrated ERP: designed AI-assisted experience reducing data entry via natural language commands.",
      "Multimedia Recognition System: trained PyTorch model to predict series metadata (title, season, type).",
      "Smart Doorbell with AI: CNN-based face recognition on Raspberry Pi with GPIO hardware control and offline capability.",
      "Custom AI model training and adaptation per client needs; service exposure via FastAPI.",
    ],
    tech: ["Python", "FastAPI", "PyTorch", "LLMs/ML", "React Native", "Raspberry Pi", "Edge ML"],
  },
  {
    company: "OpenBank",
    role: "Android Developer",
    start: "Feb 2023",
    end: "Sep 2024",
    location: "Remote · Freelance",
    bullets: [
      "Android banking application development for Amazon.",
    ],
    tech: ["Kotlin", "Coroutines", "LiveData/Flow", "Retrofit", "Moshi", "Room", "Koin", "Espresso", "JUnit"],
  },
  {
    company: "MedUx",
    role: "Full Stack Developer",
    start: "Apr 2023",
    end: "Aug 2023",
    location: "Remote · Freelance",
    bullets: [
      "Application for the Chilean government for network state measurements and monitoring.",
    ],
    tech: ["Kotlin Multiplatform", "Swift", "SwiftUI", "Jetpack Compose", "Java"],
  },
  {
    company: "Mercadona",
    role: "Full Stack Developer",
    start: "Feb 2023",
    end: "Aug 2023",
    location: "Remote · Freelance",
    bullets: [
      "Internal pilot project covering frontend and backend systems.",
    ],
    tech: ["Kotlin", "Java", "Spring Boot"],
  },
  {
    company: "Olympic Channel",
    role: "Full Stack Developer",
    start: "Feb 2023",
    end: "Aug 2023",
    location: "Remote · Freelance",
    bullets: [
      "Custom analytics library similar to Segment, replacing the existing third-party dependency.",
    ],
    tech: ["Android", "Kotlin", "Java", "Spring Boot"],
  },
  {
    company: "Atresmedia",
    role: "Android Developer",
    start: "May 2022",
    end: "Jul 2022",
    location: "Madrid (Remote)",
    bullets: [
      "Android TV and smartphone/tablet app development.",
      "Maintenance, bug fixing and CI/CD deployment support with GitLab pipelines.",
    ],
    tech: ["Kotlin", "Coroutines", "Flow", "Retrofit", "Moshi", "Room", "RxJava", "Espresso", "JUnit", "GitLab CI"],
  },
  {
    company: "Nomasystems — Inditex",
    role: "Full Stack Developer",
    start: "Oct 2019",
    end: "May 2022",
    location: "A Coruña (Remote)",
    bullets: [
      "Project built from scratch with MVVM architecture and Kotlin.",
      "Bridge between business and tech on Inditex project; digitized Excel-based factory audit processes.",
      "Contributed to Inditex internal apps — Zara and Zara Home.",
    ],
    tech: ["Kotlin", "Java", "Coroutines/Flow", "Retrofit", "Room", "Spring Boot", "Bamboo CI"],
  },
  {
    company: "Earlier Experience",
    role: "2017 – 2019",
    start: "2017",
    end: "2019",
    isEarlier: true,
    items: [
      { name: "Demesix", desc: "Android fleet management app for the taxi sector; PHP backend collaboration." },
      { name: "Pipop", desc: "Web + Android + iOS app for IoT remote device control." },
      { name: "SDEA", desc: "Full-stack development across web, Android and iOS." },
      { name: "Converfit", desc: "Web development with Node.js, MongoDB and Ajax." },
      { name: "Opirata", desc: "HTTPS migration, server changes and PHP version updates." },
    ],
  },
];

function TimelineCard({ entry }) {
  const { t } = useTranslation();

  if (entry.isEarlier) {
    return (
      <div className="exp-card exp-card--earlier">
        <div className="exp-card-header">
          <div>
            <h3 className="exp-company">{entry.company}</h3>
            <p className="exp-role">{entry.role}</p>
          </div>
        </div>
        <ul className="earlier-list">
          {entry.items.map((item, i) => (
            <li key={i}>
              <strong>{item.name}:</strong> {item.desc}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const endLabel = entry.end || t("present");

  return (
    <div className="exp-card">
      <div className="exp-card-header">
        <div className="exp-card-title">
          <h3 className="exp-company">{entry.company}</h3>
          <p className="exp-role">{entry.role}</p>
        </div>
        <div className="exp-meta">
          <span className="exp-date">{entry.start} – {endLabel}</span>
          {entry.location && <span className="exp-location">{entry.location}</span>}
        </div>
      </div>
      <ul className="exp-bullets">
        {entry.bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
      <div className="exp-tech">
        {entry.tech.map((tag, i) => (
          <span key={i} className="tech-tag">{tag}</span>
        ))}
      </div>
    </div>
  );
}

function Experience() {
  const { t } = useTranslation();

  return (
    <Container fluid className="experience-section">
      <Particle />
      <Container>
        <h1 className="project-heading">
          {t("experience")}
        </h1>
        <p className="experience-subtitle">{t("experience_subtitle")}</p>
        <div className="timeline">
          {timeline.map((entry, i) => (
            <div
              className={`timeline-entry${entry.isEarlier ? " timeline-entry--full" : ""}`}
              key={i}
            >
              <TimelineCard entry={entry} />
            </div>
          ))}
        </div>
      </Container>
    </Container>
  );
}

export default Experience;
